import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../../components/home/Footer';
import useAuthStore, { isTokenExpired } from '../../stores/authStore';
import { confirmCryptoPayment, getPaymentOrder } from './postJobApi';
import { getJobPlan } from '../../config/jobPostingPlans';
import './PostJobWizard.css';

const WALLET_HINTS = {
  eth: process.env.REACT_APP_CRYPTO_WALLET_ETH || 'Contact support for ETH wallet address',
  bnb: process.env.REACT_APP_CRYPTO_WALLET_BNB || 'Contact support for BNB wallet address',
  usdt: process.env.REACT_APP_CRYPTO_WALLET_USDT || 'Contact support for USDT wallet address',
};

function CryptoPay() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const { token, isAuthenticated } = useAuthStore();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orderId || !isAuthenticated() || !token || isTokenExpired(token)) {
        setError('Please sign in to complete crypto payment.');
        setLoading(false);
        return;
      }

      try {
        const data = await getPaymentOrder(token, orderId);
        setOrder(data.order);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [orderId, token, isAuthenticated]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError('');
    try {
      const data = await confirmCryptoPayment(token, orderId);
      if (data.order?.status === 'completed') {
        navigate(`/post-job/success?orderId=${orderId}`);
        return;
      }
      setMessage(data.message || 'Payment submitted. Your job will be published after confirmation.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const plan = getJobPlan(order?.metadata?.plan || order?.jobPostingId?.plan || 'basic');
  const crypto = order?.crypto || 'eth';

  return (
    <div className="post-job-page">
      <div className="post-job-container">
        <div className="post-job-card">
          <div className="post-job-card-header">
            <p className="post-job-eyebrow">Crypto payment</p>
            <h2>Complete your payment</h2>
            <p>Send the exact amount below, then confirm to publish your job posting.</p>
          </div>

          {loading && <p>Loading payment details...</p>}
          {error && <div className="post-job-error">{error}</div>}
          {message && <div className="post-job-success">{message}</div>}

          {order && (
            <>
              <div className="post-job-summary">
                <div>
                  <span>Amount</span>
                  <strong>${Number(order.amount).toFixed(2)} USD</strong>
                </div>
                <div>
                  <span>Cryptocurrency</span>
                  <strong>{crypto.toUpperCase()}</strong>
                </div>
                <div>
                  <span>Plan</span>
                  <strong>{plan.name}</strong>
                </div>
              </div>

              <div className="post-job-crypto-wallet">
                <label>Wallet address</label>
                <code>{WALLET_HINTS[crypto]}</code>
                <p>Include your order ID in the payment memo if possible: <strong>{orderId}</strong></p>
              </div>

              <div className="post-job-actions">
                <button type="button" className="post-job-btn secondary" onClick={() => navigate('/post-job?step=3')}>
                  Back
                </button>
                <button type="button" className="post-job-btn primary" disabled={submitting} onClick={handleConfirm}>
                  {submitting ? 'Submitting...' : 'I have sent payment'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default CryptoPay;
